import { Dialog } from 'omakase';
import { LoadingContainer } from 'ui';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { WaitlistEntry } from 'state/walkinWaitlist';
import { appPatronProfileSelector, getIsAuthenticated } from 'selectors/appSelector';
import { consumer, core } from '@tock/coretypes';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import React, { useCallback, useState } from 'react';
import WalkInWaitlistSignUpForm from 'components/business/walkin-waitlist/WalkinWaitlistSignUpForm';
import WalkinWaitlistPartyStatus from 'components/business/walkin-waitlist/WalkinWaitlistPartyStatus';

interface Props {
  business: consumer.IBusinessConfig;
  onClose(): void;
}

const WalkinWaitlistModalContent: React.FunctionComponent<Props> = ({ business, onClose }) => {
  const NO_WAITLIST_DATA_AVAILABLE = -1;

  const {
    actions: { waitlist: waitlistActions },
  } = useActions();

  const patronProfile = useSelector(appPatronProfileSelector)?.patron;
  const userIsAuthenticated = useSelector(getIsAuthenticated);
  const walkinWaitlistParty = useSelector((state: StoreType) => {
    return business?.id ? state.walkinWaitlist.entries[business.id] : null;
  });
  const showExpiredWarning: boolean = useSelector((state: StoreType) =>
    state.walkinWaitlist.businessIdsOfEntriesExpiredSinceLastReload.includes(business?.id ?? 0)
  );

  const [numPartiesAhead, setNumPartiesAhead] = useState(NO_WAITLIST_DATA_AVAILABLE);
  const [isNumPartiesAheadRequestInProgress, setIsNumPartiesAheadRequestInProgress] =
    useState(false);

  const fetchWalkinPartyPosition = useCallback(
    (entry: WaitlistEntry) => {
      setIsNumPartiesAheadRequestInProgress(true);
      waitlistActions
        .getWalkinWaitlistPartyStatus(entry.partyId)
        .then((response: ProtoResponse<core.WalkinWaitlistPartyStatus>) => {
          // Waitlist is 0-based so numPartiesAhead is also current position.
          setNumPartiesAhead(response.payload.postionOnWaitlist);
          setIsNumPartiesAheadRequestInProgress(false);
        });
    },
    [waitlistActions]
  );

  useMountEffect(() => {
    if (walkinWaitlistParty) {
      fetchWalkinPartyPosition(walkinWaitlistParty);
    }
  });

  return (
    <Dialog
      aria-labelledby="walkin-waitlist-dialog-title"
      aria-describedby="walkin-waitlist-dialog-description"
      open={true}
      onClose={onClose}
    >
      {walkinWaitlistParty ? (
        <WalkinWaitlistPartyStatus
          onClose={onClose}
          business={business}
          fetchWalkinPartyPosition={fetchWalkinPartyPosition}
          numPartiesAhead={numPartiesAhead}
          isNumPartiesAheadRequestInProgress={isNumPartiesAheadRequestInProgress}
        />
      ) : (
        <LoadingContainer loading={userIsAuthenticated && !patronProfile}>
          <WalkInWaitlistSignUpForm
            onClose={onClose}
            business={business}
            showExpiredWarning={showExpiredWarning}
            patronProfile={patronProfile}
          />
        </LoadingContainer>
      )}
    </Dialog>
  );
};

export default WalkinWaitlistModalContent;
